import * as angular from 'angular';

import {
    ComponentDefinition,
    ContextualField,
    Miniature,
    PropertyType
} from '../../../models/index';
import { Name } from '../../../common/helpers/contextual';
import { IWidget, IWidgetPosition } from '../../../widgets';
import { widgetDragged, widgetResized } from '../../../common/events';
import { maxRows } from '../dashboardConstants';

interface WidgetContextualFormController extends angular.IFormController {
    nameProperty: angular.INgModelController;
    colProperty: angular.INgModelController;
    rowProperty: angular.INgModelController;
    sizeXProperty: angular.INgModelController;
    sizeYProperty: angular.INgModelController;
    showLeftMarginProperty: angular.INgModelController;
    showRightMarginProperty: angular.INgModelController;
    showTopMarginProperty: angular.INgModelController;
    showBottomMarginProperty: angular.INgModelController;
    backgroundColorProperty: angular.INgModelController;
    dashboardLinkProperty: angular.INgModelController;
}

class ModernWidgetContextualController implements angular.IComponentController {
    formController: WidgetContextualFormController;
    miniatures: Miniature[];
    properties: ContextualField[];

    readonly cancel: () => angular.IPromise<void>;
    readonly dashboards: [];
    readonly extraMiniatures: Miniature[];
    readonly hideCommonProps: boolean;
    readonly miniatureList: Miniature[];
    readonly save: () => angular.IPromise<void>;
    readonly selectedItem: IWidget;

    private _initialPosition: IWidgetPosition;

    static $inject = ['$scope'];

    constructor(private _$scope: angular.IScope) {
    }

    $onInit() {
        const updateOptions: angular.INgModelOptions = {
            updateOn: 'default blur',
            debounce: 300
        };
        const nameField = angular.copy(Name);
        this.properties = [
            nameField,
            {
                label: 'Hide title',
                modelName: 'isTitleHidden',
                propertyName: 'hideTitleProperty',
                propertyType: PropertyType.Boolean,
                icon: 'fa-header'
            },
            {
                label: 'Row',
                modelName: 'row',
                propertyName: 'rowProperty',
                propertyType: PropertyType.Number,
                icon: 'fa-long-arrow-down',
                required: true,
                extraData: {
                    min: 0,
                    options: updateOptions
                },
                isValid: (value: string) => {
                    const parsed = parseInt(value);
                    const isValid = value === null
                        || value === undefined
                        || isNaN(parsed)
                        || parsed + (this.selectedItem.sizeY || 0) < maxRows;
                    return { 'overflowY': isValid };
                },
                messages: [
                    { key: 'required', value: 'Row is required' },
                    { key: 'min', value: 'Row must be an integer greater than or equal to 0' },
                    { key: 'float', value: 'Row must be an integer greater than or equal to 0' },
                    { key: 'overflowY', value: 'Height plus row must be an integer smaller than ' + maxRows }
                ]
            },
            {
                label: 'Column',
                modelName: 'col',
                propertyName: 'colProperty',
                propertyType: PropertyType.Number,
                icon: 'fa-long-arrow-right',
                required: true,
                extraData: {
                    min: 0,
                    max: 11,
                    options: updateOptions
                },
                isValid: (value: string) => {
                    const parsed = parseInt(value);
                    const isValid = value === null
                        || value === undefined
                        || isNaN(parsed)
                        || parsed >= 11
                        || parsed + (this.selectedItem.sizeX || 0) < 13;
                    return { 'overflowX': isValid };
                },
                messages: [
                    { key: 'required', value: 'Column is required' },
                    { key: 'min', value: 'Column must be an integer greater than or equal to 0 and smaller than 12' },
                    { key: 'max', value: 'Column must be an integer greater than or equal to 0 and smaller than 12' },
                    { key: 'float', value: 'Column must be an integer greater than or equal to 0 and smaller than 12' },
                    { key: 'overflowX', value: 'Width plus column must be an integer smaller than 13' }
                ]
            },
            {
                label: 'Width',
                modelName: 'sizeX',
                propertyName: 'sizeXProperty',
                propertyType: PropertyType.Number,
                icon: 'fa-arrows-h',
                required: true,
                extraData: {
                    min: 1,
                    max: 12,
                    options: updateOptions
                },
                isValid: (value: string) => {
                    const parsed = parseInt(value);
                    const isValid = value === null
                        || value === undefined
                        || isNaN(parsed)
                        || parsed >= 12
                        || parsed + (this.selectedItem.col || 0) < 13;
                    return { 'overflowX': isValid };
                },
                messages: [
                    { key: 'required', value: 'Width is required' },
                    { key: 'min', value: 'Width must be an integer greater than 0 and smaller than 13' },
                    { key: 'max', value: 'Width must be an integer greater than 0 and smaller than 13' },
                    { key: 'float', value: 'Width must be an integer greater than 0 and smaller than 13' },
                    { key: 'overflowX', value: 'Width plus column must be an integer smaller than 13' }
                ]
            },
            {
                label: 'Height',
                modelName: 'sizeY',
                propertyName: 'sizeYProperty',
                propertyType: PropertyType.Number,
                icon: 'fa-arrows-v',
                required: true,
                extraData: {
                    min: 1,
                    max: maxRows,
                    options: updateOptions
                },
                isValid: (value: string) => {
                    const parsed = parseInt(value);
                    const isValid = value === null
                        || value === undefined
                        || isNaN(parsed)
                        || parsed + (this.selectedItem.row || 0)< maxRows;

                    return { 'overflowY': isValid };
                },
                messages: [
                    { key: 'required', value: 'Height is required' },
                    { key: 'min', value: 'Height must be an integer greater than 0' },
                    { key: 'max', value: 'Height plus row must be an integer smaller than ' + maxRows },
                    { key: 'float', value: 'Height must be an integer greater than 0' },
                    { key: 'overflowY', value: 'Height plus row must be an integer smaller than ' + maxRows }
                ]
            },
            {
                label: 'Left margin',
                modelName: 'showLeftMargin',
                propertyName: 'showLeftMarginProperty',
                propertyType: PropertyType.Boolean,
                icon: 'fa-step-backward',
                extraData: {
                }
            },
            {
                label: 'Right margin',
                modelName: 'showRightMargin',
                propertyName: 'showRightMarginProperty',
                propertyType: PropertyType.Boolean,
                icon: 'fa-step-forward',
                extraData: {
                }
            },
            {
                label: 'Top margin',
                modelName: 'showTopMargin',
                propertyName: 'showTopMarginProperty',
                propertyType: PropertyType.Boolean,
                icon: 'fa-step-backward fa-rotate-90',
                extraData: {
                }
            },
            {
                label: 'Bottom margin',
                modelName: 'showBottomMargin',
                propertyName: 'showBottomMarginProperty',
                propertyType: PropertyType.Boolean,
                icon: 'fa-step-forward fa-rotate-90',
                extraData: {
                }
            },
            {
                label: 'Background color',
                modelName: 'backgroundColor',
                propertyName: 'backgroundColorProperty',
                propertyType: PropertyType.Color,
                icon: 'fa-square',
                extraData: {
                }
            },
            {
                label: 'Dashboard',
                modelName: 'dashboardLink',
                propertyName: 'dashboardLinkProperty',
                propertyType: PropertyType.DashboardSelector,
                icon: 'fa-th-large',
                extraData: {
                    addNone: true
                }
            }
        ];

        this._$scope.$on(widgetResized, (event, data: { sizeX: number; sizeY: number; guid: string }) => {
            if (data && this._initialPosition) {
                if (data.guid === this.selectedItem.guid) {
                    if (data.sizeX !== this._initialPosition.sizeX) {
                        this.formController.sizeXProperty.$setDirty();
                    } else {
                        this.formController.sizeXProperty.$setPristine();
                    }
                    if (data.sizeY !== this._initialPosition.sizeY) {
                        this.formController.sizeYProperty.$setDirty();
                    } else {
                        this.formController.sizeYProperty.$setPristine();
                    }
                }
            }
        });

        this._$scope.$on(widgetDragged, (event, data: { col: number; row: number; guid: string }) => {
            if (data && this._initialPosition) {
                if (data.guid === this.selectedItem.guid) {
                    if (this.formController && this.formController.colProperty) {
                        if (data.col !== this._initialPosition.col) {
                            this.formController.colProperty.$setDirty();
                        } else {
                            this.formController.colProperty.$setPristine();
                        }
                    }
                    if (this.formController && this.formController.rowProperty) {
                        if (data.row !== this._initialPosition.row) {
                            this.formController.rowProperty.$setDirty();
                        } else {
                            this.formController.rowProperty.$setPristine();
                        }
                    }
                }
            }
        });
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['extraMiniatures'] || changesObj['miniatureList']) {
            this.miniatures = (this.extraMiniatures || [])
                .concat(this.miniatureList || []);
        }
        if (changesObj['selectedItem']) {
            this.resetInitialPosition();
        }
    }

    onSave() {
        return this.save()
            .then(() => {
                this.resetInitialPosition();
            });
    }

    onCancel() {
        return this.cancel()
            .then(() => {
                this._initialPosition = null;
            });
    }

    private resetInitialPosition(): void {
        if (this.selectedItem) {
            const { col, row, sizeX, sizeY } = this.selectedItem;
            this._initialPosition = { col, row, sizeX, sizeY };
        }
    }
}

export const wtModernWidgetContextual: ComponentDefinition = {
    componentId: 'wtModernWidgetContextual',
    controller: ModernWidgetContextualController,
    template: require('./modernWidgetContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        cancel: '&',
        dashboards: '<',
        extraMiniatures: '<?',
        extraValidationFields: '<?',
        formController: '=',
        hideCommonProps: '<?',
        miniatureList: '<',
        save: '&',
        selectedItem: '<'
    },
    transclude: {
        'miniature': '?wtMiniatures',
        extraProperties: '?extraProperties'
    }
};